import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAsyncAction } from '../../hooks/useAsyncAction';
import { useFeedback } from '../../context/FeedbackContext';
import { CHECK_STATUSES } from '../../constants/appConstants';
import { getSubtotalBeforeVat } from '../../utils/checkDisplay';
import {
  effectiveDiscountPercent,
  isDiscountWithinCap,
  maxFixedDiscountForCap,
} from '../../utils/checkDiscount';
import { formatCurrency } from '../../utils/currency';

export default function CheckDiscountEditor({
  checkId,
  check,
  balance,
  isManager,
  canApplyDiscount,
  onUpdated,
  checksBasePath = '/api/checks',
}) {
  const { t } = useTranslation();
  const { showError } = useFeedback();
  const { loading, run } = useAsyncAction();

  const [discountType, setDiscountType] = useState(check?.discountType || 'none');
  const [discountPercent, setDiscountPercent] = useState(
    check?.discountPercent != null ? String(check.discountPercent) : '',
  );
  const [discountAmount, setDiscountAmount] = useState(
    check?.discountAmount != null ? String(check.discountAmount) : '',
  );

  useEffect(() => {
    setDiscountType(check?.discountType || 'none');
    setDiscountPercent(check?.discountPercent != null ? String(check.discountPercent) : '');
    setDiscountAmount(check?.discountAmount != null ? String(check.discountAmount) : '');
  }, [check?.discountType, check?.discountPercent, check?.discountAmount]);

  if (!canApplyDiscount || check?.status === CHECK_STATUSES.CLOSED) return null;

  const subtotalBeforeVat = getSubtotalBeforeVat(balance).check;
  const maxCap = !isManager && balance?.maxDiscountPercent != null
    ? Number(balance.maxDiscountPercent)
    : null;
  const maxFixed = maxCap != null ? maxFixedDiscountForCap(subtotalBeforeVat, maxCap) : null;

  const handleSave = () => {
    const payload = { discountType };
    if (discountType === 'percent') {
      payload.discountPercent = Number(discountPercent) || 0;
    } else if (discountType === 'fixed') {
      payload.discountAmount = Number(discountAmount) || 0;
    }

    if (!isDiscountWithinCap({
      discountType,
      discountPercent: payload.discountPercent,
      discountAmount: payload.discountAmount,
      subtotalBeforeVat,
      maxDiscountPercent: balance?.maxDiscountPercent,
      isManager,
    })) {
      showError(t('checks.discountExceedsMax', { percent: maxCap }));
      return;
    }

    run(async () => {
      try {
        const res = await axios.patch(`${checksBasePath}/${checkId}/discount`, payload);
        onUpdated?.(res.data);
      } catch (error) {
        showError(error.response?.data?.error || t('checks.discountSaveFailed'));
      }
    });
  };

  const capHelper = maxCap != null
    ? t('checks.discountMaxHelper', { percent: maxCap })
    : null;

  return (
    <Box sx={{ mb: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" gutterBottom>{t('checks.discountSection')}</Typography>
      <FormControl component="fieldset" size="small" sx={{ mb: 1 }}>
        <RadioGroup
          row
          value={discountType}
          onChange={(e) => setDiscountType(e.target.value)}
        >
          <FormControlLabel value="none" control={<Radio size="small" />} label={t('checks.discountNone')} />
          <FormControlLabel value="percent" control={<Radio size="small" />} label={t('checks.discountTypePercent')} />
          <FormControlLabel value="fixed" control={<Radio size="small" />} label={t('checks.discountTypeFixed')} />
        </RadioGroup>
      </FormControl>
      <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="flex-start">
        {discountType === 'percent' && (
          <TextField
            size="small"
            type="number"
            label={t('checks.discountPercentLabel')}
            value={discountPercent}
            onChange={(e) => setDiscountPercent(e.target.value)}
            inputProps={{ min: 0, max: maxCap ?? 100, step: 0.1 }}
            helperText={capHelper}
            sx={{ width: 160 }}
          />
        )}
        {discountType === 'fixed' && (
          <TextField
            size="small"
            type="number"
            label={t('checks.discountFixedLabel', { currency: balance?.checkCurrency || '' })}
            value={discountAmount}
            onChange={(e) => setDiscountAmount(e.target.value)}
            inputProps={{ min: 0, max: maxFixed ?? subtotalBeforeVat, step: 0.01 }}
            helperText={maxFixed != null
              ? `${capHelper} (${formatCurrency(maxFixed, balance?.checkCurrency)})`
              : capHelper}
            sx={{ width: 200 }}
          />
        )}
        <Button
          size="small"
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          sx={{ mt: 0.25 }}
        >
          {loading ? t('common.saving') : t('checks.applyDiscount')}
        </Button>
      </Box>
      {discountType !== 'none' && subtotalBeforeVat > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {t('checks.discountEffectivePercent', {
            percent: effectiveDiscountPercent(
              discountType,
              Number(discountPercent) || 0,
              Number(discountAmount) || 0,
              subtotalBeforeVat,
            ),
          })}
        </Typography>
      )}
    </Box>
  );
}
